import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getVendor } from "../../redux/Action";
import axios from '../../redux/axiosInstance';
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import "./ViewVendors.css";

const Vendors = () => {
  const apiUrl = process.env.REACT_APP_API;
  const dispatch = useDispatch();
  const { vendors, error } = useSelector((state) => state.getVendor);
  const user = useSelector((state) => state.user?.user || state.admin?.admin || {});
  const actorName = user.name || user.email || "Admin";
  const actorEmail = user.email || "";
  const fileInputRef = useRef(null);

  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [isLogPanelOpen, setIsLogPanelOpen] = useState(false);
  const [logVendor, setLogVendor] = useState(null);
  const [currentVendor, setCurrentVendor] = useState(null);
  const [newDocuments, setNewDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState("details");

  const initialForm = {
    organizationName: "",
    legalEntityName: "",
    vendorType: "",
    businessCategory: "",
    registrationNumber: "",
    gstTaxId: "",
    registeredAddress: "",
    country: "",
    state: "",
    city: "",
    primaryContactName: "",
    contactEmail: "",
    contactPhone: "",
    website: "",
    productsServices: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    branchName: "",
    status: "Pending",
    compliance: {
      registeredBusiness: false,
      taxCompliant: false,
      isoCertified: false,
      gdprCompliant: false,
      ndaSigned: false,
      insuranceAvailable: false,
    },
  };

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    dispatch(getVendor(true));
  }, [dispatch]);

  const handleEditClick = (vendor) => {
    setCurrentVendor(vendor);
    setNewDocuments([]);
    setActiveTab("details");
    setFormData({
      organizationName: vendor.organizationName || vendor.companyname || "",
      legalEntityName: vendor.legalEntityName || "",
      vendorType: vendor.vendorType || "",
      businessCategory: vendor.businessCategory || "",
      registrationNumber: vendor.registrationNumber || "",
      gstTaxId: vendor.gstTaxId || vendor.gstNo || "",
      registeredAddress: vendor.registeredAddress || vendor.street || "",
      country: vendor.country || "",
      state: vendor.state || "",
      city: vendor.city || "",
      primaryContactName: vendor.primaryContactName || vendor.name || "",
      contactEmail: vendor.contactEmail || vendor.primaryemail || "",
      contactPhone: vendor.contactPhone || vendor.primarycontactNo || "",
      website: vendor.website || "",
      productsServices: vendor.productsServices || "",
      bankName: vendor.bankName || "",
      accountNumber: vendor.accountNumber || "",
      ifscCode: vendor.ifscCode || "",
      branchName: vendor.branchName || "",
      status: vendor.status || "Pending",
      compliance: vendor.compliance || initialForm.compliance,
    });
    setIsEditPopupOpen(true);
  };

  const handleDeleteClick = (vendor) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete vendor ${vendor.organizationName}?`
    );
    if (confirmed) handleDelete(vendor);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleComplianceChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      compliance: { ...prev.compliance, [name]: checked },
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setNewDocuments((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeNewFile = (index) => {
    setNewDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const truncateText = (text, maxLength) => {
    if (text?.length > maxLength) return `${text.substring(0, maxLength)}...`;
    return text || "-";
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = new FormData();
      payload.append("updatedByName", actorName);
      payload.append("updatedByEmail", actorEmail);
      
      Object.entries(formData).forEach(([key, value]) => {
        if (key === "compliance") {
          payload.append(key, JSON.stringify(value));
        } else {
          payload.append(key, value);
        }
      });
      newDocuments.forEach((file) => payload.append("documents", file));

      await axios.put(`${apiUrl}/vendors/${currentVendor._id}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      dispatch(getVendor(true));
      setIsEditPopupOpen(false);
      toast.success("Vendor updated successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Error updating vendor");
    }
  };

  const handleDelete = async (vendor) => {
    try {
      await axios.delete(`${apiUrl}/account/${vendor._id}`, {
        data: {
          removedBy: actorName,
          removedByEmail: actorEmail,
        },
      });
      dispatch(getVendor(true));
      toast.success("Vendor deleted successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Error deleting vendor");
    }
  };

  const handleViewDocument = async (fileUrl) => {
    try {
      // Extract the exact blob name from the file URL
      const blobName = fileUrl.substring(fileUrl.lastIndexOf("/") + 1);
      const response = await axios.post(`${apiUrl}/attachment/${encodeURIComponent(blobName)}`);
      
      if (response.data && response.data.sasUrl) {
        window.open(response.data.sasUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Failed to get document URL");
      }
    } catch (error) {
      console.error("Error fetching SAS URL:", error);
      toast.error("Error accessing the document. Please try again.");
    }
  };

  const vendorTypes = ["Supplier", "Contractor", "Consultant", "Service Provider", "Distributor", "Other"];
  const businessCategories = [
    "IT & Software", "Manufacturing", "Logistics", "Healthcare", "Construction",
    "Financial Services", "Retail", "Consulting", "Energy", "Telecommunications", "Other",
  ];

  return (
    <div className="vendors-page">
      <div className="vendors-header-section" style={{marginTop:"30px"}}  >
        <div className="vendors-title-block">
          <h2 className="vendors-heading">Vendor Directory</h2>
          <p className="vendors-subtitle">Manage and monitor all registered vendors</p>
        </div>
      </div>

      {error && <div className="vendors-error-banner"><p>Error: {error}</p></div>}

      <div className="vendors-table-card">
        <div className="table-responsive">
          <table className="vendors-table">
            <thead>
              <tr>
                <th>Sl No</th>
                <th>Organization</th>
                <th>Vendor Type</th>
                <th>Contact</th>
                <th>Location</th>
                <th>GST / Tax ID</th>
                <th>Status</th>
                <th className="action-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendors && vendors.length > 0 ? (
                vendors.map((vendor, index) => (
                  <tr key={vendor._id} className={`vendors-row ${vendor.isDeleted ? "vendor-row-removed" : ""}`}>
                    <td className="vendors-cell">{index + 1}</td>
                    <td className="vendors-cell font-medium">{truncateText(vendor.organizationName || vendor.companyname, 25)}</td>
                    <td className="vendors-cell">
                      <span className="type-badge">{truncateText(vendor.vendorType, 20)}</span>
                    </td>
                    <td className="vendors-cell contact-cell">
                      <div className="contact-email">{truncateText(vendor.contactEmail || vendor.primaryemail, 25)}</div>
                      <div className="contact-phone">{vendor.contactPhone || vendor.primarycontactNo}</div>
                    </td>
                    <td className="vendors-cell">{`${vendor.city || ""}, ${vendor.state || ""}, ${vendor.country || ""}`}</td>
                    <td className="vendors-cell">{vendor.gstTaxId || vendor.gstNo || "-"}</td>
                    <td className="vendors-cell">
                      <span className={`status-badge status-${vendor.isDeleted ? "removed" : (vendor.status?.toLowerCase() || "pending")}`}>
                        {vendor.isDeleted ? "Removed" : (vendor.status || "Pending")}
                      </span>
                    </td>
                    <td className="vendors-cell actions-cell">
                      {!vendor.isDeleted && (
                        <button className="action-btn edit-btn" onClick={() => handleEditClick(vendor)} title="Edit Vendor">
                          <EditIcon fontSize="small" />
                        </button>
                      )}
                      <button className="action-btn log-btn" onClick={() => { setLogVendor(vendor); setIsLogPanelOpen(true); }} title="Activity Log">
                        <HistoryIcon fontSize="small" />
                      </button>
                      {!vendor.isDeleted && (
                        <button className="action-btn delete-btn" onClick={() => handleDeleteClick(vendor)} title="Delete Vendor">
                          <DeleteIcon fontSize="small" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", padding: "24px", color: "#64748b" }}>
                    No vendors found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ========== Edit Modal ========== */}
      {isEditPopupOpen && (
        <div className="edit-modal-backdrop" onClick={() => setIsEditPopupOpen(false)}>
          <div className="edit-modal-content" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSubmit} className="edit-form-modern">
              {/* Modal Header */}
              <div className="modal-header">
                <h3>Edit Vendor Details</h3>
                <button type="button" className="close-btn" onClick={() => setIsEditPopupOpen(false)}>×</button>
              </div>

              {/* Tabs */}
              <div className="modal-tabs">
                <button type="button" className={`modal-tab ${activeTab === "details" ? "active" : ""}`} onClick={() => setActiveTab("details")}>
                  Details
                </button>
                <button type="button" className={`modal-tab ${activeTab === "documents" ? "active" : ""}`} onClick={() => setActiveTab("documents")}>
                  Documents {currentVendor?.documents?.length > 0 && <span className="tab-badge">{currentVendor.documents.length}</span>}
                </button>
              </div>

              {/* Modal Body */}
              <div className="modal-body">

                {/* ===== TAB: Details ===== */}
                {activeTab === "details" && (
                  <div className="edit-form-grid">
                    <label>Organization Name:
                      <input type="text" name="organizationName" value={formData.organizationName} onChange={handleInputChange} />
                    </label>
                    <label>Legal Entity Name:
                      <input type="text" name="legalEntityName" value={formData.legalEntityName} onChange={handleInputChange} />
                    </label>
                    <label>Vendor Type:
                      <select name="vendorType" value={formData.vendorType} onChange={handleInputChange}>
                        <option value="">Select</option>
                        {vendorTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </label>
                    <label>Business Category:
                      <select name="businessCategory" value={formData.businessCategory} onChange={handleInputChange}>
                        <option value="">Select</option>
                        {businessCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </label>
                    <label>Registration Number:
                      <input type="text" name="registrationNumber" value={formData.registrationNumber} onChange={handleInputChange} />
                    </label>
                    <label>GST / Tax ID:
                      <input type="text" name="gstTaxId" value={formData.gstTaxId} onChange={handleInputChange} />
                    </label>
                    <label style={{ gridColumn: "1 / -1" }}>Registered Address:
                      <input type="text" name="registeredAddress" value={formData.registeredAddress} onChange={handleInputChange} />
                    </label>
                    <label>Country:
                      <input type="text" name="country" value={formData.country} onChange={handleInputChange} />
                    </label>
                    <label>State:
                      <input type="text" name="state" value={formData.state} onChange={handleInputChange} />
                    </label>
                    <label>City:
                      <input type="text" name="city" value={formData.city} onChange={handleInputChange} />
                    </label>
                    <label>Primary Contact Name:
                      <input type="text" name="primaryContactName" value={formData.primaryContactName} onChange={handleInputChange} />
                    </label>
                    <label>Contact Email:
                      <input type="email" name="contactEmail" value={formData.contactEmail} onChange={handleInputChange} />
                    </label>
                    <label>Contact Phone:
                      <input type="text" name="contactPhone" value={formData.contactPhone} onChange={handleInputChange} />
                    </label>
                    <label>Website:
                      <input type="url" name="website" value={formData.website} onChange={handleInputChange} />
                    </label>
                    <label style={{ gridColumn: "1 / -1" }}>Products / Services:
                      <input type="text" name="productsServices" value={formData.productsServices} onChange={handleInputChange} />
                    </label>
                    <label>Bank Name:
                      <input type="text" name="bankName" value={formData.bankName} onChange={handleInputChange} />
                    </label>
                    <label>Account Number:
                      <input type="text" name="accountNumber" value={formData.accountNumber} onChange={handleInputChange} />
                    </label>
                    <label>IFSC Code:
                      <input type="text" name="ifscCode" value={formData.ifscCode} onChange={handleInputChange} />
                    </label>
                    <label>Branch Name:
                      <input type="text" name="branchName" value={formData.branchName} onChange={handleInputChange} />
                    </label>
                    <label>Status:
                      <select name="status" value={formData.status} onChange={handleInputChange}>
                        <option value="Pending">Pending</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </label>

                    {/* Compliance Section */}
                    <div className="compliance-section">
                      <h4 className="section-subtitle">Compliance Status</h4>
                      <div className="compliance-grid">
                        {[
                          { key: "registeredBusiness", label: "Registered Business" },
                          { key: "taxCompliant", label: "Tax Compliant" },
                          { key: "isoCertified", label: "ISO Certified" },
                          { key: "gdprCompliant", label: "GDPR Compliant" },
                          { key: "ndaSigned", label: "NDA Signed" },
                          { key: "insuranceAvailable", label: "Insurance Available" },
                        ].map((item) => (
                          <label key={item.key} className="compliance-item">
                            <input
                              type="checkbox"
                              name={item.key}
                              checked={formData.compliance[item.key] || false}
                              onChange={handleComplianceChange}
                            />
                            <span>{item.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ===== TAB: Documents ===== */}
                {activeTab === "documents" && (
                  <div className="documents-tab">
                    {/* Existing Documents */}
                    <h4 className="section-subtitle">Uploaded Documents</h4>
                    {currentVendor?.documents && currentVendor.documents.length > 0 ? (
                      <div className="doc-list">
                        {currentVendor.documents.map((doc, i) => (
                          <div key={i} className="doc-item">
                            <div className="doc-icon">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                              </svg>
                            </div>
                            <div className="doc-details">
                              <span className="doc-name">{doc.fileName}</span>
                              <span className="doc-type">{doc.fileType}</span>
                            </div>
                            <button 
                              type="button" 
                              onClick={() => handleViewDocument(doc.fileUrl)} 
                              className="doc-view-btn"
                            >
                              View
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state">
                        <p>No documents uploaded yet</p>
                      </div>
                    )}

                    {/* Upload New Documents */}
                    <h4 className="section-subtitle" style={{ marginTop: "20px" }}>Upload New Documents</h4>
                    <div
                      className="upload-zone"
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("dragover"); }}
                      onDragLeave={(e) => { e.currentTarget.classList.remove("dragover"); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove("dragover");
                        setNewDocuments((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
                      }}
                    >
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <span>Click or drag files to upload</span>
                      <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileChange} style={{ display: "none" }} />
                    </div>

                    {newDocuments.length > 0 && (
                      <div className="new-files-list">
                        {newDocuments.map((file, i) => (
                          <div key={i} className="new-file-item">
                            <span className="new-file-name">{file.name}</span>
                            <span className="new-file-size">{formatFileSize(file.size)}</span>
                            <button type="button" className="remove-file-btn" onClick={() => removeNewFile(i)}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

              </div>

              {/* Modal Footer */}
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setIsEditPopupOpen(false)}>Cancel</button>
                <button type="submit" className="btn-save">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========== Activity Log Side Panel ========== */}
      {isLogPanelOpen && logVendor && (
        <div className="log-panel-backdrop" onClick={() => setIsLogPanelOpen(false)}>
          <div className="log-panel" onClick={(e) => e.stopPropagation()}>
            <div className="log-panel-header">
              <div>
                <h3>Activity Log</h3>
                <p className="log-panel-vendor">{logVendor.organizationName}</p>
              </div>
              <button type="button" className="close-btn" onClick={() => setIsLogPanelOpen(false)}>×</button>
            </div>
            <div className="log-panel-body">
              {logVendor.activityLog && logVendor.activityLog.length > 0 ? (
                <div className="activity-list">
                  {logVendor.activityLog.slice().reverse().map((log, i) => (
                    <div key={i} className="activity-item">
                      <div className={`activity-dot ${log.action === "Created" ? "dot-green" : log.action === "Removed" ? "dot-red" : "dot-blue"}`}></div>
                      <div className="activity-content">
                        <div className="activity-header">
                          <strong>{log.action}</strong>
                          <span className="activity-time">{new Date(log.date).toLocaleString()}</span>
                        </div>
                        <div className="activity-by">
                          by {log.by || log.byEmail || "System"}
                          {log.byEmail && log.byEmail !== log.by && <span> ({log.byEmail})</span>}
                        </div>
                        {log.details && <div className="activity-details">{log.details}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No activity recorded yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vendors;
